/**
 * MockAdCreative — A realistic product ad using a real Unsplash photo.
 * Uses a brandless product on clean light background.
 * Unsplash License: Free for commercial use, no attribution required.
 *
 * Props:
 *   showHeatmap: boolean — overlay attention heatmap
 *   size: "sm" | "md" | "lg"
 *   className: string
 */
export default function MockAdCreative({ showHeatmap = false, size = "md", className = "" }) {
  const s = size === "sm";
  const l = size === "lg";

  return (
    <div className={`relative w-full aspect-[4/3] rounded-xl overflow-hidden select-none ${className}`}
      style={{ background: "linear-gradient(170deg, #FFFBF5 0%, #FFF7ED 40%, #FEF3E2 100%)" }}>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col">

        {/* Top bar */}
        <div className={`flex items-center justify-between ${s ? "px-2.5 pt-2" : l ? "px-6 pt-5" : "px-4 pt-3"}`}>
          <div className="flex items-center gap-1.5">
            <div className={`rounded-full bg-slate-800 flex items-center justify-center font-black text-white ${s ? "w-3.5 h-3.5 text-[5px]" : l ? "w-7 h-7 text-[10px]" : "w-5 h-5 text-[7px]"}`}
              style={{ fontFamily: "Outfit, sans-serif" }}>S</div>
            <span className={`font-bold text-slate-700 tracking-wide ${s ? "text-[6px]" : l ? "text-xs" : "text-[8px]"}`}
              style={{ fontFamily: "Outfit, sans-serif" }}>
              SOLARA
            </span>
          </div>
          <span className={`rounded-full font-bold uppercase tracking-wider text-white ${s ? "text-[4px] px-1.5 py-px" : l ? "text-[9px] px-3 py-1" : "text-[6px] px-2 py-0.5"}`}
            style={{ background: "linear-gradient(135deg, #F97316, #EC4899)" }}>
            New Arrival
          </span>
        </div>

        {/* Product image */}
        <div className="flex-1 flex items-center justify-center relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&q=80&auto=format&fit=crop"
            alt="Product"
            className="object-contain"
            style={{
              maxHeight: s ? "62%" : l ? "72%" : "68%",
              maxWidth: "80%",
              filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.1))",
            }}
            loading="eager"
            draggable="false"
          />
        </div>

        {/* Bottom: text + CTA */}
        <div className={`${s ? "px-2.5 pb-2 gap-0.5" : l ? "px-6 pb-5 gap-2" : "px-4 pb-3 gap-1"} flex flex-col`}>
          <div>
            <h3 className={`font-bold text-slate-800 leading-tight ${s ? "text-[8px]" : l ? "text-base" : "text-[11px]"}`}
              style={{ fontFamily: "Outfit, sans-serif" }}>
              Classic Timepiece
            </h3>
            <p className={`text-slate-500 leading-snug ${s ? "text-[5px]" : l ? "text-[11px] mt-0.5" : "text-[7px]"}`}>
              Minimalist design. Premium craftsmanship.
            </p>
          </div>
          <div className="flex items-center justify-between">
            <span className={`font-bold text-slate-800 ${s ? "text-[7px]" : l ? "text-sm" : "text-[9px]"}`}
              style={{ fontFamily: "JetBrains Mono, monospace" }}>
              $249
            </span>
            <div className={`rounded-lg text-white font-semibold ${s ? "text-[5px] px-1.5 py-0.5" : l ? "text-[11px] px-4 py-1.5" : "text-[7px] px-2.5 py-1"}`}
              style={{ background: "linear-gradient(135deg, #F97316, #EC4899)" }}>
              Shop Now
            </div>
          </div>
        </div>
      </div>

      {/* Heatmap overlay */}
      {showHeatmap && (
        <div className="absolute inset-0 z-20 pointer-events-none" style={{
          background: `
            radial-gradient(ellipse 42% 30% at 50% 43%, rgba(239, 68, 68, 0.55) 0%, rgba(239, 68, 68, 0.15) 50%, transparent 70%),
            radial-gradient(ellipse 35% 12% at 50% 82%, rgba(239, 68, 68, 0.45) 0%, rgba(245, 158, 11, 0.1) 50%, transparent 70%),
            radial-gradient(ellipse 18% 10% at 72% 90%, rgba(245, 158, 11, 0.45) 0%, transparent 65%),
            radial-gradient(ellipse 14% 9% at 14% 7%, rgba(245, 158, 11, 0.35) 0%, transparent 65%),
            radial-gradient(ellipse 12% 8% at 88% 7%, rgba(16, 185, 129, 0.3) 0%, transparent 65%),
            radial-gradient(ellipse 25% 14% at 50% 7%, rgba(16, 185, 129, 0.2) 0%, transparent 65%),
            radial-gradient(ellipse 10% 10% at 10% 90%, rgba(16, 185, 129, 0.2) 0%, transparent 65%),
            radial-gradient(ellipse 10% 10% at 90% 50%, rgba(16, 185, 129, 0.15) 0%, transparent 65%)
          `,
        }} />
      )}
    </div>
  );
}
